import type { Progress } from "ky";
import type { RefOrGetter } from "ls/common/types";
import type { ShortcutJs } from "@/generated/models";
import { addArtboard } from "ls/api/cloud/expo";
import { computed, readonly, ref, toValue } from "vue";
import { asError, uuid } from "@/common/lib";

type ClickthroughUploadBatchSource = "web" | "desktop";
type ClickthroughUploadStatus = "idle" | "exporting" | "pending" | "success" | "error" | "canceled";

export interface ClickthroughUploadBatch {
  id: string;
  shortcut: string;
  shortcutName: string;
  created: Date;
  status: ClickthroughUploadStatus;
  source: ClickthroughUploadBatchSource;
  progress?: Progress;
  abort: AbortController["abort"];
  signal: AbortSignal;
  error?: Error;
}

interface BatchFile {
  name: string;
}

const batches = ref(new Map<string, ClickthroughUploadBatch>());
const batchFiles = ref(new WeakMap<ClickthroughUploadBatch, BatchFile[]>());

export function useBatchFiles(batch: RefOrGetter<ClickthroughUploadBatch>) {
  return computed(() => batchFiles.value.get(toValue(batch)));
}

export function useClickthroughUploader() {
  async function newUpload(file: ShortcutJs, source: ClickthroughUploadBatchSource, files: File[]) {
    const controller = new AbortController();
    const batchId = uuid();
    const batch: ClickthroughUploadBatch = {
      id: batchId,
      source,
      created: new Date(),
      status: "idle",
      shortcut: file.Shortcut,
      shortcutName: file.Name,
      abort: controller.abort,
      signal: controller.signal,
    };
    batchFiles.value.set(batch, files.map(f => ({
      name: f.name,
    })));
    batches.value.set(batchId, batch);

    await upload(batch, files);
  }

  function update(batchId: string, update: Partial<ClickthroughUploadBatch>) {
    const batch = batches.value.get(batchId);
    if (!batch) return;

    const newBatch = {
      ...batch,
      ...update,
    };

    batches.value.set(batchId, newBatch);

    // make sure to keep files around in weakmap
    const files = batchFiles.value.get(batch);
    if (files) {
      batchFiles.value.set(newBatch, files);
    }
  }

  async function upload(batch: ClickthroughUploadBatch, nativeFiles: File[]) {
    try {
      update(batch.id, { status: "pending" });
      await addArtboard({ shortcut: batch.shortcut, files: nativeFiles, skipPublishNotes: true }, {
        signal: batch.signal,
        onUploadProgress(progressEvent) {
          update(batch.id, { progress: progressEvent });
        },
      });
      update(batch.id, { status: "success" });
    } catch (error) {
      update(batch.id, {
        status: "error",
        error: asError(error),
      });
    }
  }

  function dismiss(batchId: string) {
    const batch = batches.value.get(batchId);
    if (!batch) return;

    batches.value.delete(batchId);
  }

  async function newWebUpload(shortcut: ShortcutJs, files: File[]) {
    await newUpload(shortcut, "web", files);
  }

  async function newDesktopUpload(batchId: string, data: {
    shortcut: string;
    shortcutName: string;
    files: BatchFile[];
  }) {
    const controller = new AbortController();
    const batch: ClickthroughUploadBatch = {
      id: batchId,
      source: "desktop",
      created: new Date(),
      status: "idle",
      shortcut: data.shortcut,
      shortcutName: data.shortcutName,
      abort: controller.abort,
      signal: controller.signal,
    };
    batchFiles.value.set(batch, data.files);
    batches.value.set(batchId, batch);
  }

  return {
    batches: readonly(batches),
    batchFiles: readonly(batchFiles),
    newWebUpload,
    newDesktopUpload,
    update,
    dismiss,
  };
}
